import clsx from 'clsx'
import IntLink from 'components/common/IntLink'
import NextButton from 'components/common/NextButton'
import PrevButton from 'components/common/PrevButton'
import { BLUR_DATA_URL, MEDIA_URL } from 'config/constants'
import { useKeenSlider } from 'keen-slider/react'
import { isAbsent, isEmptyArray, isPresent } from 'lib/utils'
import Image from 'next/image'
import { useState } from 'react'
import { type IServicePortfolio } from 'store/api'

type Props = {
  section: {
    label: string
    items: IServicePortfolio[]
  }
  currentSlug?: string
}

const PortfolioSlider = ({ section, currentSlug }: Props) => {
  const [maxIndex, setMaxIndex] = useState(0)

  const activeIdx =
    section?.items?.findIndex((e) => e.slug === currentSlug) ?? -1

  let [ref, slider] = useKeenSlider<HTMLDivElement>({
    initial: activeIdx > 0 ? activeIdx : 0,
    slides: {
      perView: 1.75,
      spacing: 8,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: {
          perView: 2.5,
          spacing: 8,
        },
      },
      '(min-width: 1280px)': {
        slides: {
          perView: 4.5,
          spacing: 12,
        },
      },
    },
    loop: true,
    created(slider) {
      setMaxIndex(slider.track.details.maxIdx)
    },
    slideChanged(slider) {
      setMaxIndex(slider.track.details.maxIdx)
    },
  })

  if (isAbsent(section?.items) || isEmptyArray(section?.items))
    return null

  return (
    <div className='bg-transparent mt-12'>
      <div className='flex items-center justify-between z-10 mb-6'>
        <div className='text-xs lg:text-base font-bold uppercase font-unbounded leading-tight'>
          {section.label}
        </div>

        <div className='hidden md:flex gap-4 justify-end flex-1 pr-4 xl:pr-12'>
          <PrevButton
            active={maxIndex > 0}
            onClick={() => slider.current?.prev()}
          />
          <NextButton
            active={maxIndex > 0}
            onClick={() => slider.current?.next()}
          />
        </div>
      </div>

      <div className='keen-slider z-10' ref={ref}>
        {isPresent(section.items) &&
          section.items.length > 0 &&
          section.items.map((e) => (
            <div
              key={e.id}
              className={clsx(
                'keen-slider__slide rounded-[12px] bg-lightGrey',
                e.slug === currentSlug && 'border-2 border-accent'
              )}
            >
              <IntLink
                className='group text-xs lg:text-lg leading-tighter text-dark font-semibold'
                href={`/catering/events/${e.slug}`}
              >
                <div className='aspect-w-3 aspect-h-2 order-1'>
                  <Image
                    placeholder='blur'
                    blurDataURL={BLUR_DATA_URL}
                    src={`${MEDIA_URL}${e.preview}`}
                    alt=''
                    layout='fill'
                    objectFit='cover'
                    sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px'
                  />
                </div>

                <div className='h-12 lg:h-[4.5rem] group pt-2.5 lg:pt-3 px-3 lg:px-6'>
                  <div className='group-hover:text-accent line-clamp-2'>
                    {e.name}
                  </div>
                </div>
              </IntLink>
            </div>
          ))}
      </div>
    </div>
  )
}

export default PortfolioSlider
